import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  GaugeIcon,
  RectangleHorizontalIcon,
  RectangleVerticalIcon,
  SquareIcon,
  TimerIcon,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type BaseProps = {
  id?: string;
  value: string;
  options?: string[];
  width?: number | string | 'auto';
  className?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

type VideoParamSelectorProps = BaseProps & {
  icon?: ReactNode;
  getIcon?: (value: string) => ReactNode;
  getLabel?: (value: string) => ReactNode;
  type?: string;
};

function VideoParamSelector({
  id,
  value,
  options,
  width = 120,
  className,
  onChange,
  disabled,
  icon,
  getIcon,
  getLabel,
  type = 'video-param',
}: VideoParamSelectorProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);

  const opts = useMemo(() => {
    const list = (options && options.length ? options : ['Auto']).map(String);
    // 去重，避免重复选项导致 Combobox key/value 冲突
    return Array.from(new Set(list));
  }, [options]);

  const renderLabel = (v: string) => {
    if (v === 'Auto') return <div className="whitespace-nowrap">{t('Auto') || 'Auto'}</div>;
    return getLabel ? getLabel(v) : <div className="whitespace-nowrap">{v}</div>;
  };

  const safeValue = String(value || opts[0] || 'Auto');
  const renderIcon = (v: string) => {
    if (getIcon) return getIcon(v);
    return icon ?? null;
  };

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={opts.map((option) => ({ label: option, value: option }))}
      type={type}
      value={safeValue}
      onValueChange={(val) => {
        onChange?.(val);
        setOpen(false);
      }}
    >
      <ComboboxTrigger
        id={id}
        variant="ghost"
        className={cn('rounded-xl h-9 px-3 hover:bg-sidebar-accent transition-all border-none font-medium', className)}
        style={width === 'auto' ? {} : { width }}
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2 min-w-0">
            {renderIcon(safeValue)}
            <div className="min-w-0 truncate">{renderLabel(safeValue)}</div>
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
        </div>
      </ComboboxTrigger>

      <ComboboxContent
        className={cn('p-0', className)}
        popoverOptions={{ className: 'fm-toolbar-surface rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}
      >
        <ComboboxList className="custom-scrollbar">
          <ComboboxEmpty />
          <ComboboxGroup className="p-1">
            {opts.map((option) => (
              <ComboboxItem
                key={option}
                value={option}
                onSelect={() => {
                  onChange?.(option);
                  setOpen(false);
                }}
                className="rounded-lg py-2 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2"
              >
                {renderIcon(option)}
                {renderLabel(option)}
                <CheckIcon
                  className={cn(
                    'ml-auto size-4 text-primary',
                    String(safeValue) === String(option) ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function getAspectRatioIcon(option: string) {
  // match / Auto: fall back to square icon (consistent with ImageSizeSelector)
  if (option === 'match' || option === 'Auto') {
    return <SquareIcon size={16} className="text-muted-foreground shrink-0" />;
  }

  const raw = String(option || '').trim();
  if (!raw) return <SquareIcon size={16} className="text-muted-foreground shrink-0" />;

  // Support "W:H", "W/H", and "WxH"
  const delimiter = raw.includes(':') ? ':' : raw.includes('/') ? '/' : raw.includes('x') ? 'x' : undefined;
  if (!delimiter) return <SquareIcon size={16} className="text-muted-foreground shrink-0" />;

  const [wRaw, hRaw] = raw.split(delimiter);
  const w = Number(wRaw);
  const h = Number(hRaw);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return <SquareIcon size={16} className="text-muted-foreground shrink-0" />;
  }

  if (w === h) return <SquareIcon size={16} className="text-muted-foreground shrink-0" />;
  if (w > h) return <RectangleHorizontalIcon size={16} className="text-muted-foreground shrink-0" />;
  return <RectangleVerticalIcon size={16} className="text-muted-foreground shrink-0" />;
}

function getAspectRatioLabel(option: string) {
  if (option === 'match') return <div className="whitespace-nowrap">Auto</div>;
  if (option === 'Auto') return <div className="whitespace-nowrap">Auto</div>;
  return <div className="whitespace-nowrap">{option}</div>;
}

export function VideoAspectRatioSelector(props: BaseProps) {
  const { t } = useI18n();
  return (
    <VideoParamSelector
      {...props}
      getIcon={(v) => getAspectRatioIcon(v)}
      type="video-aspect-ratio"
      getLabel={(v) => {
        // Keep i18n for Auto (existing behavior), and treat "match" as Auto in UI
        if (v === 'Auto' || v === 'match') return <div className="whitespace-nowrap">{t('Auto') || 'Auto'}</div>;
        return getAspectRatioLabel(v);
      }}
    />
  );
}

export function VideoDurationSelector(props: BaseProps) {
  return (
    <VideoParamSelector
      {...props}
      icon={<TimerIcon size={16} className="text-muted-foreground shrink-0" />}
      type="video-duration"
      getLabel={(v) => <div className="whitespace-nowrap">{v}s</div>}
    />
  );
}

export function VideoQualitySelector(props: BaseProps) {
  return (
    <VideoParamSelector
      {...props}
      icon={<GaugeIcon size={16} className="text-muted-foreground shrink-0" />}
      type="video-quality"
    />
  );
}



